import { useCallback, useState } from 'react';

import { request } from '../../shared/api';
import { ListSkeleton, renderFailure } from '../../shared/states';
import { useLoad } from '../../shared/use-load';
import { useUpgrade } from '../../shared/upgrade';
import { OnboardingScreen } from '../onboarding/OnboardingScreen';
import { NotificationsScreen } from '../profile/NotificationsScreen';

/**
 * Радар: подписки и что по ним нашлось (T11.4, `09_UI_UX.md` §4.1).
 *
 * Экран открытия. Первым делом здесь стоит число находок — оно и есть
 * то, за что платят: сервис продаёт не список объявлений, а то, что
 * нашёл их раньше остальных.
 *
 * ## Счётчик считает сервер
 *
 * `new_count` приходит из ответа, а не складывается на клиенте.
 * Непрочитанность определяется отметкой в базе, и второй подсчёт
 * разошёлся бы с первым при первом же уведомлении, пришедшем во время
 * загрузки экрана.
 *
 * ## Пустое состояние — это онбординг
 *
 * Экран без подписок бесполезен, и сделать с ним можно ровно одно —
 * завести первую. Поэтому вместо пустоты с кнопкой здесь стоит сам
 * онбординг (T11.11): человек отвечает на два вопроса и включает
 * уведомления, не совершив ни одного лишнего перехода.
 */

/** Подписка в том виде, в каком её отдаёт `/v1/subscriptions`. */
export interface Subscription {
  readonly id: string | number;
  readonly name: string;
  readonly filters: Record<string, unknown>;
  readonly is_enabled: boolean;
  readonly is_active: boolean;
  readonly paused_until: string | null;
  readonly last_matched_at: string | null;
  readonly new_count: number;
}

/** Цена в рублях без копеек: копейки в цене автомобиля не значат ничего. */
function price(minor: unknown): string | null {
  return typeof minor === 'number' && minor > 0
    ? `${Math.round(minor / 100).toLocaleString('ru-RU')} ₽`
    : null;
}

/**
 * Короткое описание фильтров.
 *
 * Не все поля, а те, по которым человек узнаёт свою подписку. Полный
 * перечень занял бы три строки на каждую и превратил бы список в
 * простыню — а список нужен, чтобы выбрать взглядом.
 */
export function describeFilters(filters: Record<string, unknown>): string {
  const parts: string[] = [];
  const from = price(filters['price_min_minor']);
  const to = price(filters['price_max_minor']);

  if (from !== null && to !== null) parts.push(`${from} — ${to}`);
  else if (to !== null) parts.push(`до ${to}`);
  else if (from !== null) parts.push(`от ${from}`);

  const mileage = filters['mileage_max_km'];

  if (typeof mileage === 'number' && mileage > 0) {
    parts.push(`до ${Math.round(mileage / 1000)} тыс. км`);
  }

  const owners = filters['owners_max'];

  if (typeof owners === 'number' && owners > 0) parts.push(`владельцев: ${owners}`);

  return parts.join(' · ');
}

/** Состояние подписки одним знаком: список читается взглядом. */
export function stateMark(subscription: Subscription): string {
  if (!subscription.is_enabled) return '⏸';

  return subscription.is_active ? '🔔' : '💤';
}

/** На сколько останавливается подписка одним нажатием. */
export const PAUSE_DAYS = 7;

/** Момент окончания паузы, какого ждёт `POST /v1/subscriptions/:id/pause`. */
export function pauseUntil(from: Date = new Date()): string {
  return new Date(from.getTime() + PAUSE_DAYS * 24 * 60 * 60 * 1000).toISOString();
}

/**
 * Строка подписки с действиями над ней.
 *
 * Действия стоят прямо здесь, а не за переходом в карточку. Экран был
 * только списком: останавливать и удалять API умеет с T7.9, а нажать на
 * это было негде — человек видел свои подписки и не мог с ними ничего
 * сделать.
 *
 * Удаление спрашивает подтверждения, остановка — нет. Разница в
 * обратимости: остановленную подписку возвращает то же нажатие, а
 * удалённую не вернёт ничто, и вместе с ней уходит история находок.
 */
function Row({
  subscription,
  onChanged,
  onOpen,
}: {
  subscription: Subscription;
  onChanged: () => void;
  onOpen: (subscription: Subscription) => void;
}): React.JSX.Element {
  const filters = describeFilters(subscription.filters);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const paused = !subscription.is_enabled;
  const id = String(subscription.id);

  const act =
    (run: () => Promise<unknown>): (() => void) =>
    (): void => {
      if (busy) return;

      setBusy(true);
      void run()
        .then(onChanged)
        // Отказ не роняет экран: список остаётся прежним, и человек
        // повторит нажатие. Без сообщения — единственная внятная
        // причина отказа здесь «нет сети», а сказать это яснее, чем
        // неизменившийся список, всё равно нечем
        .catch(() => undefined)
        .finally(() => setBusy(false));
    };

  return (
    <div className="card subscription">
      {/*
        Заголовок — кнопка, а не подпись. Радар копит объявления, и
        посмотреть его улов было негде: перечень подписок открывался, а
        внутрь не пускал. Счётчик рядом обещал содержимое, которого
        нельзя было достичь ни одним касанием
      */}
      <button type="button" className="subscription__head" onClick={() => onOpen(subscription)}>
        <span className="subscription__name">
          {stateMark(subscription)} {subscription.name}
        </span>
        {subscription.new_count > 0 ? (
          <span className="subscription__count">{subscription.new_count}</span>
        ) : null}
        <span className="subscription__chevron" aria-hidden="true">
          ›
        </span>
      </button>
      {filters === '' ? null : <p className="subscription__filters">{filters}</p>}

      <div className="subscription__actions">
        <button
          type="button"
          className="button button--quiet"
          disabled={busy}
          onClick={act(() =>
            paused
              ? request(`/v1/subscriptions/${id}/resume`, { method: 'POST' })
              : request(`/v1/subscriptions/${id}/pause`, {
                  method: 'POST',
                  body: { until: pauseUntil() },
                }),
          )}
        >
          {paused ? 'Возобновить' : 'Остановить'}
        </button>

        {confirming ? (
          <>
            <button
              type="button"
              className="button button--danger"
              disabled={busy}
              onClick={act(() => request(`/v1/subscriptions/${id}`, { method: 'DELETE' }))}
            >
              Удалить насовсем
            </button>
            <button
              type="button"
              className="button button--quiet"
              disabled={busy}
              onClick={() => setConfirming(false)}
            >
              Отмена
            </button>
          </>
        ) : (
          <button
            type="button"
            className="button button--quiet"
            disabled={busy}
            onClick={() => setConfirming(true)}
          >
            Удалить
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Итог по всем подпискам.
 *
 * Крупное число вверху — единственное место, где видно работу сервиса за
 * ночь. Без него экран выглядит списком настроек, а не радаром.
 */
function Total({ count }: { count: number }): React.JSX.Element {
  return (
    <div className="card radar">
      <span className="radar__count">{count}</span>
      <span className="radar__label">
        {count === 0 ? 'новых пока нет' : 'новых по вашим подпискам'}
      </span>
    </div>
  );
}

export function SubscriptionsScreen({
  /** Переход на «Поиск»: там подписка и создаётся. */
  onCreate,
}: {
  onCreate?: () => void;
} = {}): React.JSX.Element {
  const onUpgrade = useUpgrade();
  const [opened, setOpened] = useState<Subscription | null>(null);
  const [state, retry] = useLoad<Subscription[]>(
    useCallback(() => request<Subscription[]>('/v1/subscriptions'), []),
  );

  if (state.kind === 'loading') return <ListSkeleton rows={3} />;

  if (state.kind === 'failed') {
    return <>{renderFailure(state.error, { onRetry: retry, onUpgrade })}</>;
  }

  // Пустое состояние — это и есть онбординг (T11.4, T11.11), а не
  // карточка с кнопкой, ведущей в него. Кнопка стоила бы человеку
  // четвёртого действия сверх трёх, отпущенных `02_SPEC.md` §5.1, и
  // ровно одного повода закрыть приложение: экран, на котором нечего
  // делать, кроме как нажать «начать», не сообщает, ради чего начинать
  if (state.value.length === 0) {
    return <OnboardingScreen {...(onCreate === undefined ? {} : { onSearch: onCreate })} />;
  }

  const total = state.value.reduce((sum, one) => sum + one.new_count, 0);

  if (opened !== null) {
    return (
      <NotificationsScreen
        subscriptionId={String(opened.id)}
        backLabel="← К радарам"
        onBack={() => {
          setOpened(null);
          // Счётчик непрочитанных мог измениться, пока человек смотрел
          // улов: вернуться к прежнему числу значило бы показать
          // неправду
          retry();
        }}
      />
    );
  }

  return (
    <>
      <Total count={total} />
      {state.value.map((subscription) => (
        <Row
          key={String(subscription.id)}
          subscription={subscription}
          onChanged={retry}
          onOpen={setOpened}
        />
      ))}
    </>
  );
}
